import React, { useEffect, useRef, useState } from "react";
import logo from "icons/smallLogo.svg";
import {
  calculateDiffSeconds,
  cardToShortname,
  encode,
  formatCurrency,
  getGeneralDetailMarkers,
  getOperatingSystem,
  isExpired,
  useOutsideClick,
} from "utils/HelperFunctions";
import {
  addNotification,
  updateUserData,
  useUserData,
} from "store/slices/authSlice";
import { EditIcon } from "icons/payments/EditIcon";
import ScannerImg from "../../icons/bi_qr-code.png";
import Skeleton from "react-loading-skeleton";
import moment from "moment";
import Countdown from "components/ui-component/Countdown";
import PaymentMethods from "components/actionCenter/PaymentMethods";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { goToInvalid } from "utils/invalidRoute";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  duplicatePaymentGet,
  getPaymentLink,
  makeGooglePay,
  markIncompletePayment,
  paymentDownload,
  payPalOneTimeCreate,
  payPalOneTimeExecute,
  processLink,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  setIsPaymentProcessing,
  storePayPalOrder,
  updateLogoAndFavicon,
  updatePaymentPermission,
  verifyPaymentsViaStax,
  verifyWalletPay,
  wirePaymentProcess,
} from "store/slices/paymentsSlice";
import {
  useLeadData,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import PaymentLinkExpired from "components/actionCenter/PaymentLinkExpired";
import PaymentLinkSuccess from "components/actionCenter/PaymentLinkSuccess";
import PaymentLinkProcessSuccess from "components/actionCenter/PaymentLinkProcessSuccess";
import PaymentLinkProcessFailed from "components/actionCenter/PaymentLinkProcessFailed";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  useIsLogin,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import WirePaymentSuccess from "components/actionCenter/WirePaymentSuccess";
import Footer from "components/layout/footer";
import { formatPaymentListDate } from "utils/DateFormat";
import CustomAmountInput from "components/ui-component/CustomAmountInput";
import CustomModal from "components/ui-component/CustomModal";
import DisclosureInitial from "components/makePayment/DisclosureInitial";
import { decode } from "utils/HelperFunctions";
import { FormRef, SettingsValue } from "types/payments";
import { RootState } from "store/store";
import { useAmountValidation } from "hooks/useAmountValidation";
import { useCountdown } from "hooks/useCountdown";
import {
  PaymentLinkData,
  PaymentProcessStatus,
  WireDetails,
  GooglePaySelection,
} from "types/paymentLink";
import { useNavigationGuard } from "hooks/useNavigationGuard";
import { BASE_URL } from "store/api/constApi";
import { VerifyStripeCardPaymentParams } from "types/agreement";
import { TestIdScope, useTestAttrsFactory } from "utils/testid";
import MessageToast from "components/ui-component/MessageToast";
import {
  linkAccountMismatchMessage,
  signOutOtherAccount,
  takeLinkAccountMismatch,
} from "utils/linkAccountMismatch";
import {
  decodeAndValidatePublicLink,
  isInvalidPublicLinkResult,
} from "utils/publicLinkValidation";

const PaymentLink = () => {
  const userData: any = useUserData();
  const accountLeadData = useLeadData();
  const testAttrs = useTestAttrsFactory();
  const portalSettings = usePortalSettingsData();
  const { brandColor, isDark } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const dispatch = useAppDispatch();
  const {
    payPalOrder,
    generalSettings,
    conFee,
    checkedDuplicatePayment,
    isPaymentProcessing,
  } = useAppSelector((state: RootState) => state.payment);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const isLogin = useIsLogin();
  const [selectedMethod, setSelectedMethod] = useState<
    "Card" | "Bank" | "PayPal" | "Cash App Pay" | string
  >("Card");
  const showFinalDisclosure = useShowFinalDisclosure();
  const cashAppRef = useRef<FormRef>(null);
  const bankRef = useRef<FormRef>(null);
  const cardRef = useRef<FormRef>(null);
  const amazonRef = useRef<FormRef>(null);
  const applePayVerificationInFlightRef = useRef<Set<string>>(new Set());
  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const [tokenData, setTokenData] = useState<GooglePaySelection | null>(null);
  const { id: actionId } = useParams();
  const paymentLinkId = decodeAndValidatePublicLink(actionId, "pl_")?.recordId;
  const [initialDisclosureAccept, setInitialDisclosureAccept] =
    useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isLinkSent, setIsLinkSent] = useState<boolean>(false);
  const [requestStatus, setRequestStatus] = useState<string>("Pending");
  const [isLinkSentSuccess, setIsLinkSentSuccess] = useState<boolean>(false);
  const [isLinkSentFailedReason, setIsLinkSentFailedReason] =
    useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [paymentLinkData, setPaymentLinkData] =
    useState<PaymentLinkData | null>(null);
  const [timeDiff, setTimeDiff] = useState<number | null>(null);
  const seconds = useCountdown(timeDiff);
  const [paymentProcess, setPaymentProcess] =
    useState<PaymentProcessStatus>(null);
  const [email, setEmail] = useState("");
  const [invoiceId, setInvoiceId] = useState<string>("");

  const scrollPaymentSectionToTop = (): void => {
    const scrollToTop = () => {
      let parent = paymentSectionRef.current?.parentElement;

      while (parent) {
        if (parent.scrollHeight > parent.clientHeight) {
          parent.scrollTo({ top: 0, behavior: "auto" });
        }
        parent = parent.parentElement;
      }

      paymentSectionRef.current?.scrollTo({ top: 0, behavior: "auto" });
      document.scrollingElement?.scrollTo({ top: 0, behavior: "auto" });
      window.scrollTo({ top: 0, behavior: "auto" });
    };

    scrollToTop();
    requestAnimationFrame(scrollToTop);
    window.setTimeout(scrollToTop, 100);
  };
  const [amount, setAmount] = useState("");
  const [isPaymentLinkBTNLoading, setIsPaymentLinkBTNLoading] =
    useState<boolean>(false);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(false);
  const [isStaxPayPalLoading, setIsStaxPayPalLoading] =
    useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(true);
  const [failedReason, setFailedReason] = useState<string | null>(null);
  const [isWirePayment, setIsWirePayment] = useState<boolean>(false);
  const [isManualForm, setIsManualForm] = useState<boolean>(true);
  const [showModal, setShowModal] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [wireDetails, setWireDetails] = useState<WireDetails>({
    email: "",
    bank: "",
    routing: "",
    account: "",
    swift: "",
    dueDate: "",
  });
  const [paymentLogo, setPaymentLogo] = useState<string>(logo);
  const [qrCode, seQrCode] = useState<string | null>(null);
  const [editVal, setEditVal] = useState<string>("0.00");
  const valueRef = useRef<string>(editVal);
  valueRef.current = editVal;

  const amtInputContainerRef = useRef<HTMLDivElement | null>(null);

  const { amtErr, setAmtErr, validateAmount } = useAmountValidation(
    generalSettings.systemSettings,
    userData?.data?.accountData?.balance
  );

  useOutsideClick(amtInputContainerRef, () => {
    setIsEdit(validateAmount(valueRef.current));
  });

  const handleBackToStart = () => {
    setAmount("");
    setIsEdit(false);
    setIsLinkSent(false);
    setIsLoading(true);
    setEditVal("0.00");
    setPaymentLinkData(null);
    setTimeDiff(null);
    setPaymentProcess(null);
    setEmail("");
    setInvoiceId("");
    setFailedReason(null);
    getPaymentLinkData();
    dispatch(onSetFinalDisclosureLoading(false));
    dispatch(onSetShowDisclosure(false));
    setInitialDisclosureAccept(false);
  };

  const { allowPendingNavigation, cancelPendingNavigation } =
    useNavigationGuard({
      shouldBlock: () => isPaymentProcessing,
      onNavigationAttempt: () => setShowModal(true),
    });

  const handleConfirmLeave = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    dispatch(setIsPaymentProcessing(false));
    setShowModal(false);
    setTimeout(() => allowPendingNavigation(), 250);
  };

  const handleCancelLeave = () => {
    setShowModal(false);
    cancelPendingNavigation();
  };

  useEffect(() => {
    dispatch(setIsPaymentProcessing(paymentProcess === "true"));
    return () => {
      dispatch(setIsPaymentProcessing(false));
    };
  }, [paymentProcess, dispatch]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isPaymentProcessing) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isPaymentProcessing]);

  const handleClick = (e: any) => {
    e.stopPropagation();
    setIsEdit(true);
  };

  // seconds countdown now handled by useCountdown(timeDiff)
  useEffect(() => {
    if (isLogin && paymentLinkData && userData) {
      const accounts_id = paymentLinkData?.accounts_id;
      const id = userData?.data?.accountData?.id;

      if (id && accounts_id && id !== accounts_id) {
        signOutOtherAccount(dispatch);
      }
    }
  }, [paymentLinkData, isLogin, userData]);

  // After signing out a different account, say why the page reloaded.
  const [mismatchNotice, setMismatchNotice] = useState<string | null>(null);
  useEffect(() => {
    if (takeLinkAccountMismatch()) {
      setMismatchNotice(linkAccountMismatchMessage("payment link"));
    }
  }, []);

  const handleStore = async ({
    paymentLinkData,
    customerData,
    companyLogoDetails,
    paymentPermission,
  }: any) => {
    setPaymentLinkData(paymentLinkData);
    dispatch(updatePaymentPermission(paymentPermission));
    setPaymentLogo(companyLogoDetails?.logo);
    dispatch(updateLogoAndFavicon(companyLogoDetails));
    paymentLinkData?.is_paid === "N" &&
      setEditVal(`${paymentLinkData?.amount}`);
    dispatch(
      updateUserData({
        ...userData?.data?.accountData,
        ...customerData,
      })
    );
  };

  const getPaymentLinkData = async () => {
    const decodedLink = decodeAndValidatePublicLink(actionId, "pl_");
    if (!decodedLink) {
      goToInvalid(navigate, "link");
      return;
    }

    try {
      let payload = { slug: actionId };
      let res: any = await dispatch(getPaymentLink(payload)).unwrap();

      if (isInvalidPublicLinkResult(res)) {
        goToInvalid(navigate, "link");
        return;
      }

      const { data, success } = res?.body;
      if (success) {
        const { paymentLinkData } = data;
        // A wire transfer set up from this link has not arrived yet: show that
        // payment's page (bank instructions) rather than offering to pay again.
        if (paymentLinkData?.is_paid === "N" && data?.wirePending?.slug) {
          navigate(`/view-payment/${data.wirePending.slug}`, { replace: true });
          return;
        }

        seQrCode(data?.qrcode);
        await handleStore(data);

        const diff = calculateDiffSeconds(
          new Date(),
          paymentLinkData?.expiry_date
        );
        setTimeDiff(parseInt(diff?.toFixed(0)));
        setIsLinkSent(data?.requested || false);
        setRequestStatus(data?.requestedStatus || "Pending");
        if (paymentLinkData?.is_paid === "Y") {
          setEmail(paymentLinkData?.email);
          setInvoiceId(paymentLinkData?.order_id);
          if (paymentLinkData?.orderDetails) {
            let amount =
              (paymentLinkData?.orderDetails?.amount +
                (paymentLinkData?.orderDetails?.cc_fee_amount ||
                  paymentLinkData?.orderDetails?.ach_fee_amount)) ||
              paymentLinkData?.amount;

            setEditVal(amount);
            setAmount(amount);

            // if(paymentLinkData?.orderDetails?.payment_method === "WTO") {
            //   setIsWirePayment(true);
            //   setWireDetails({
            //     email: paymentLinkData?.email || "",
            //     bank: paymentLinkData?.orderDetails?.bank_name || "",
            //     routing: paymentLinkData?.orderDetails?.routing_number || "",
            //     account: paymentLinkData?.orderDetails?.account_number || "",
            //     swift: paymentLinkData?.orderDetails?.swift_code || "",
            //   });

            // }
          }
          setPaymentProcess("true");
        } else {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_link",
              userid: data?.customerData?.leadData?.user_id,
              email: "",
              event_status: "clicked",
              activity_type: "payment_activity",
              clusterid: decodedLink.clusterId,
              communication_status: "",
              accounts_id: paymentLinkData?.accounts_id,
              payment_amount: paymentLinkData?.amount,
              payment_link_id: decodedLink.recordId,
            })
          );
        }
      }
      setIsLoading(false);
    } catch (error: any) {
      if (isInvalidPublicLinkResult(error)) {
        goToInvalid(navigate, "link");
        return;
      }
      console.log("get logo err: -->", error?.message);
    }
  };

  const handleLegacyGooglePay = async (tokenData: any, total_amount: any) => {
    let slug = decode(actionId || "")?.split("_");
    try {
      dispatch(setIsPaymentProcessing(true));
      setIsGoogleLoading(true);
      const { tokenizationData } = tokenData;
      let resp = JSON.parse(tokenizationData.token);
      let payload = {
        slug: actionId || "",
        email: userData?.data?.accountData?.email || "",
        stripe_token: resp?.id,
        phone_number: userData?.data?.accountData?.phone,
        first_name: userData?.data?.accountData?.first_name || "",
        last_name: userData?.data?.accountData?.last_name || "",
        accountid: userData?.data?.accountData?.accountid || "",
        total_amount: total_amount,
        clusterid: slug[1],
        address1: resp?.card?.address_line1 || "",
        address2: resp?.card?.address_line2 || "",
        city: resp?.card?.address_city || "",
        state: resp?.card?.address_state || "",
        zip: resp?.card?.address_zip || "",
        country: resp?.card?.address_country || "",
        is_recurring: "N",
        lead_id: paymentLinkData?.lead_id,
        module: "payment_link",
        description: "Online Payment",
        payment_note: "One-time",
        x_payment_method: "GP",
        currency: "usd",
        card_type: resp?.card?.brand || "",
        cc_last4: resp?.card?.last4 || "",
        cc_expiry: resp?.card?.exp_month + "/" + resp?.card?.exp_year || "",
        payment_link: slug[0],
        isPaymentLink: true,
      };
      let res: any = await dispatch(makeGooglePay(payload));
      if (res?.payload?.body.success) {
        setPaymentProcess("true");
        setInvoiceId(res?.payload?.body?.data?.invoice_id);
        setEmail(res?.payload?.body?.data?.email);
        setAmount(res?.payload?.body?.data?.total_amount);
        setEditVal(res?.payload?.body?.data?.total_amount);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "succeeded",
            activity_type: "payment_activity",
            clusterid: userData?.data?.accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: res?.payload?.body?.data?.amount,
            order_id: res?.payload?.body?.data?.invoice_id,
            payment_link_id: paymentLinkId,
          })
        );
        let pay = {
          invoice_id: res?.payload?.body?.data?.invoice_id,
          emails: [res?.payload?.body?.data?.email],
        };
        await sendEmail(pay);
        setIsGoogleLoading(false);
      } else {
        setFailedReason(res?.payload?.body?.data?.message);
        setPaymentProcess("false");
        setIsGoogleLoading(false);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "failed",
            activity_type: "payment_activity",
            clusterid: userData?.data?.accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: res?.payload?.body?.data?.amount,
            order_id: res?.payload?.body?.data?.invoice_id,
            payment_link_id: paymentLinkId,
          })
        );
      }
    } catch (error: any) {
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      setIsGoogleLoading(false);
      console.log("handleGooglePay error=====", error);
    } finally {
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(setIsPaymentProcessing(false));
    }
  };

  useEffect(() => {
    getPaymentLinkData();
  }, [actionId]);

  useEffect(() => {
    if (
      portalSettings &&
      portalSettings?.virtual_debt_collector === SettingsValue.YES &&
      paymentLinkData
    ) {
    }
  }, [editVal, actionId, paymentLinkData]);

  const sendEmail = async ({ invoice_id, emails, shouldSendAll }: any) => {
    try {
      const validEmails = Array.isArray(emails)
        ? emails
            .map((email: any) =>
              typeof email === "string" ? email.trim() : ""
            )
            .filter(Boolean)
        : [];

      if (
        !userData?.data?.accountData?.accountid ||
        !invoice_id ||
        validEmails.length === 0
      ) {
        console.warn("sendEmail skipped: missing required values", {
          accountid: userData?.data?.accountData?.accountid,
          invoice_id,
          emails,
        });
        return;
      }

      let payload = {
        invoice_id: invoice_id,
        accountid: userData?.data?.accountData?.accountid,
        emails: validEmails,
        shouldSendAll: shouldSendAll,
      };
      await dispatch(paymentDownload(payload)).unwrap();
    } catch (error) {
      console.log("sendEmail", error);
    }
  };

  const createPayload = (values: any, isCCTokenAvailable: boolean = false) => {
    let slug = decode(actionId || "")?.split("_");
    let payload;

    if (values?.isAddNewCard) {
      const { dataPayment } = values;

      let method = "CC";
      if (
        dataPayment?.selectedBank &&
        Object.keys(dataPayment.selectedBank).length > 0
      ) {
        method = "ACH";
      }
      if (
        dataPayment?.selectedWallet &&
        Object.keys(dataPayment.selectedWallet).length > 0
      ) {
        method = "WT";
      }

      let selectedData;
      if (method === "CC") {
        selectedData = dataPayment?.selectedCard;
      } else if (method === "WT") {
        selectedData = dataPayment?.selectedWallet;
        method = selectedData?.payment_method;
      } else {
        selectedData = dataPayment?.selectedBank;
      }

      payload = {
        slug: actionId || "",
        isPaymentLink: true,
        lead_id: paymentLinkData?.lead_id,
        cluster_id: paymentLinkData?.clusterid,
        agent: userData?.data?.accountData?.lead_owner,
        balance: userData?.data?.accountData?.balance,
        lead_owner: userData?.data?.accountData?.lead_owner,
        x_payment_method: method,
        city: selectedData?.billing_city,
        address1: selectedData?.billing_address,
        address2: selectedData?.billing_address2,
        zipcode: selectedData?.billing_zip,
        state: selectedData?.billing_state,
        country: selectedData?.billing_country,
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name,
        accountid: userData?.data?.accountData?.accountid,
        amount: Number(editVal),
        email: values?.savedCardsEmail,
        amount_type: "amount",
        use_old_card: "1",

        // card
        card_type: method === "CC" ? dataPayment?.selectedCard?.cc_type : "",
        old_cardnumber:
          method === "CC" ? dataPayment?.selectedCard?.cc_4digit : "",
        old_cvv: method === "CC" ? dataPayment?.selectedCard?.cc_cvv : "",
        old_expiry: method === "CC" ? dataPayment?.selectedCard?.cc_expiry : "",
        old_4digit: method === "CC" ? dataPayment?.selectedCard?.cc_4digit : "",
        old_type: method === "CC" ? dataPayment?.selectedCard?.cc_type : "",
        old_zip: method === "CC" ? dataPayment?.selectedCard.billing_zip : "",
        cvv: method === "CC" ? dataPayment?.selectedCard?.cc_cvv : "",
        cexpiry: method === "CC" ? dataPayment?.selectedCard?.cc_expiry : "",
        ccardnumber:
          method === "CC" ? dataPayment?.selectedCard?.cc_4digit : "",
        choldername:
          method === "CC" ? dataPayment?.selectedCard?.account_name : "",

        description: "Online Payment",
        payment_note: "One-time",
        customer_device: getOperatingSystem(),
        //bank
        aholdername:
          method === "ACH" ? dataPayment?.selectedBank?.account_name : "",
        routing_number:
          method === "ACH" ? dataPayment?.selectedBank?.routing_number : "",
        account_number:
          method === "ACH" ? dataPayment?.selectedBank?.account_number : "",
        bank_name: method === "ACH" ? dataPayment?.selectedBank?.bank_name : "",
        bank_address: "",
        payment_link: slug[0],
        cc_token: selectedData?.cc_token,
        stripe_token: selectedData?.cc_token,
        cc_token2: selectedData?.cc_token2,
        phone_number: userData?.data?.accountData?.phone || "",
      };
    } else {
      payload = {
        slug: actionId || "",
        isPaymentLink: true,
        lead_id: paymentLinkData?.lead_id,
        cluster_id: paymentLinkData?.clusterid,
        agent: userData?.data?.accountData?.lead_owner,
        balance: userData?.data?.accountData?.balance,
        lead_owner: userData?.data?.accountData?.lead_owner,
        x_payment_method: values?.x_payment_method,
        city: values?.x_city || userData?.data?.accountData?.city,
        address1: values?.x_address || userData?.data?.accountData?.address1,
        address2: values?.address2 || userData?.data?.accountData?.address2,
        zipcode: values?.x_zip || userData?.data?.accountData?.postal_code,
        state: values?.x_state || userData?.data?.accountData?.state,
        country: values?.x_country || userData?.data?.accountData?.country,
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name,
        accountid: userData?.data?.accountData?.accountid,
        amount: editVal,
        email: values?.email,
        amount_type: "amount",
        use_old_card: isCCTokenAvailable ? "1" : "0",
        phone_number: userData?.data?.accountData?.phone || "",
        // card
        card_type: values?.x_payment_method === "CC" ? values?.iconType : "",
        old_cardnumber:
          values?.x_payment_method === "CC"
            ? values?.x_card_num?.replaceAll(" ", "")
            : "",
        old_cvv: values?.x_payment_method === "CC" ? values?.x_card_code : "",
        old_expiry: values?.x_payment_method === "CC" ? values?.x_exp_date : "",
        old_4digit:
          values?.x_payment_method === "CC"
            ? values?.x_card_num?.slice(-4)
            : "",
        old_type: values?.x_payment_method === "CC" ? values?.iconType : "",
        old_zip: values?.x_payment_method === "CC" ? values?.x_zip : "",
        cvv: values?.x_payment_method === "CC" ? values?.x_card_code : "",
        cexpiry: values?.x_payment_method === "CC" ? values?.x_exp_date : "",
        ccardnumber:
          values?.x_payment_method === "CC"
            ? values?.x_card_num?.replaceAll(" ", "")
            : "",
        choldername: values?.choldername,

        description: "Online Payment",
        payment_note: "One-time",
        customer_device: getOperatingSystem(),

        aholdername: values?.choldername,
        routing_number:
          values?.x_payment_method === "ACH" ? values?.routing_number : "",
        account_number:
          values?.x_payment_method === "ACH" ? values?.account_number : "",
        bank_name: values?.x_payment_method === "ACH" ? values?.bankName : "",
        bank_address: values?.x_payment_method === "ACH" ? values?.bank : "",
        payment_link: slug[0],
        cc_token: isCCTokenAvailable ? values?.cc_token : "",
        cc_token2: isCCTokenAvailable ? values?.cc_token2 : "",
        pc_atid: values?.pc_atid,
        cppid: isCCTokenAvailable ? values?.cppid : 0,
      };
    }
    return payload;
  };

  const paymentViaBankOrCard = async (
    values: any,
    isCCTokenAvailable: boolean = false
  ) => {
    try {
      const payload = await createPayload(values, isCCTokenAvailable);
      const res: any = await dispatch(processLink(payload)).unwrap();
      if (res.body.success) {
        const { data } = res.body;
        let emails = [];
        emails.push(data?.email);
        setEmail(data?.email);
        setAmount(data?.total_amount);
        setEditVal(data?.total_amount);
        setInvoiceId(data?.invoice_id);
        let pay = {
          emails: emails,
          invoice_id: data.invoice_id,
        };
        await sendEmail(pay);
        setPaymentProcess("true");
        setIsPaymentLinkBTNLoading(false);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "succeeded",
            activity_type: "payment_activity",
            clusterid: userData?.data?.accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: editVal,
            order_id: data.invoice_id,
            payment_link_id: paymentLinkId,
          })
        );
        return true;
      } else {
        setFailedReason(
          res?.body?.data?.msg || res?.body?.data?.message || "Payment failed"
        );
        setPaymentProcess("false");
        setIsPaymentLinkBTNLoading(false);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "failed",
            activity_type: "payment_activity",
            clusterid: userData?.data?.accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: editVal,
            order_id: res?.body?.data.invoice_id,
            payment_link_id: paymentLinkId,
          })
        );
        return false;
      }
    } catch (error: any) {
      setIsPaymentLinkBTNLoading(false);
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      console.log("paymentViaBankOrCard error =====", error);
      return false;
    }
  };

  const paymentViaWire = async () => {
    let slug = decode(actionId || "")?.split("_");
    try {
      let payload = {
        slug: actionId || "",
        payment_gateway_id: "",
        amount: parseFloat(editVal).toFixed(2),
        clusterid:
          paymentLinkData?.clusterid ||
          slug?.[1] ||
          userData?.data?.accountData?.clusterid,
        x_method: "WTO",
        lead_id: paymentLinkData?.lead_id,
        email: userData?.data?.accountData?.email,
        accountid: userData?.data?.accountData?.accountid,
        isPaymentLink: true,
        payment_link: slug[0],
        description: "Online Payment",
        payment_note: "One-time",
        ivr_type: "CUST_PRTL",
      };
      let res: any = await dispatch(wirePaymentProcess(payload)).unwrap();
      if (res.body.success) {
        setWireDetails({
          email: res?.body?.data?.email || "",
          bank: res?.body?.data?.bank_name || "",
          routing: res?.body?.data?.routing_number || "",
          account: res?.body?.data?.account_number || "",
          swift: res?.body?.data?.swift_code || "",
          dueDate:
            moment(
              moment().add(res?.body?.data?.wire_deadline || 0, "days")
            ).format("MM/DD/YYYY") || "",
        });
        setInvoiceId(res?.body?.data?.invoice_id);
        setIsWirePayment(true);
        setPaymentProcess("true");
        setIsPaymentLinkBTNLoading(false);
        let pay = {
          invoice_id: res?.body?.data?.invoice_id,
          emails: [res?.body?.data?.email],
        };
        await sendEmail(pay);
        return true;
      } else {
        setFailedReason(res?.body?.data?.msg);
        setPaymentProcess("false");
        setIsPaymentLinkBTNLoading(false);
        return false;
      }
    } catch (error) {
      setIsPaymentLinkBTNLoading(false);
      setPaymentProcess("false");
      setFailedReason("Payment failed");
      console.log(" paymentViaWire error=====", error);
      return false;
    }
  };

  const processPaymentLink = async (values: any) => {
    dispatch(setIsPaymentProcessing(true));
    setIsPaymentLinkBTNLoading(true);
    try {
      if (values?.x_payment_method === "WTO") {
        await paymentViaWire();
      } else {
        await paymentViaBankOrCard(values, false);
      }
    } finally {
      dispatch(setIsPaymentProcessing(false));
    }
  };

  useEffect(() => {
    if (paymentProcess === "true") {
      getPaymentLinkData();
    }
    if (paymentProcess !== null) {
      dispatch(setIsPaymentProcessing(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentProcess]);

  const checkPastAmount = async (method?: string) => {
    try {
      let payload = {
        accounts_id: userData?.data?.accountData?.id,
        payment_method: method ? method : cardToShortname(selectedMethod),
        amount: editVal,
      };
      let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
      const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
      if (isDuplicateOrders && duplicateOrdersCount > 0) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const handleCheckDisclosureAndDuplicate = async ({
    method,
  }: {
    method?: string;
  }) => {
    try {
      let moveAhead = !checkedDuplicatePayment
        ? await checkPastAmount(method)
        : true;
      if (!moveAhead) {
        setIsLoading(false);
        dispatch(onSetFinalDisclosureLoading(false));
        return false;
      } else {
        if (
          (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
            portalSettings?.payment_agreement_opt === SettingsValue.DEFAULT) &&
          !showFinalDisclosure
        ) {
          setIsLoading(false);
          dispatch(onSetFinalDisclosureLoading(false));
          dispatch(resetDuplicateOrder());
          dispatch(onSetShowDisclosure(true));

          return false;
        } else {
          dispatch(resetDuplicateOrder());
          dispatch(resetCheckDuplicatePayment());
          return true;
        }
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  //pay pal
  const createPayPalIntent = async () => {
    dispatch(setIsPaymentProcessing(true));
    let slug = decode(actionId || "")?.split("_");
    try {
      let payload = {
        slug: actionId || "",
        lead_id: paymentLinkData?.lead_id,
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name,
        phone_number: userData?.data?.accountData?.phone,
        amount: parseFloat(editVal),
        agent: userData?.data?.accountData?.lead_owner,
        lead_owner: userData?.data?.accountData?.lead_owner,
        clusterid: userData?.data?.accountData?.clusterid,
        accountid: userData.data.accountData.accountid,
        module: "process_payment",
        success_url: window.location.href + "?status=success",
        cancel_url: window.location.href + "?status=cancel",
        isPaymentLink: true,
        payment_link: slug[0],
        is_recurring: "N",
      };
      let res: any = await dispatch(payPalOneTimeCreate(payload));
      if (res?.payload?.body?.success) {
        await dispatch(
          storePayPalOrder({
            url: res?.payload?.body?.result?.url,
            orderId: res?.payload?.body?.result?.invoice_id,
            amount: editVal,
          })
        );
        let moveAhead = await handleCheckDisclosureAndDuplicate({
          method: "PP",
        });
        if (!moveAhead) {
          dispatch(setIsPaymentProcessing(false));
          return res;
        }
        dispatch(setIsPaymentProcessing(false));
        window.location.href = res?.payload?.body?.result?.url;
      }
      return res;
    } catch (error) {
      console.log("createPayPalIntent error =====", error);
    } finally {
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const handleApprove = async (data: any) => {
    let slug = decode(actionId || "")?.split("_");
    try {
      let payload = {
        slug: actionId || "",
        payerId: data.payerID,
        paymentId: data.paymentId,
        amount: payPalOrder.amount,
        order_id: payPalOrder.orderId,
        link_id: slug[0],
        cluster_id: slug[1],
        accountid: userData?.data?.accountData?.accountid || "",
        description: "Online Payment",
        payment_note: "One-time",
      };

      let res: any = await dispatch(payPalOneTimeExecute(payload));
      if (res?.payload?.body?.success) {
        setInvoiceId(res?.payload?.body?.data?.invoice_id);
        setEmail(userData?.data?.accountData?.email);
        setAmount(payPalOrder.amount);
        setEditVal(payPalOrder.amount);
        let pay = {
          emails: [userData?.data?.accountData?.email],
          invoice_id: payPalOrder.orderId,
        };
        await sendEmail(pay);
        setPaymentProcess("true");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "succeeded",
            activity_type: "payment_activity",
            clusterid: userData?.data?.accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: res?.payload?.body?.data?.amount,
            order_id: res?.payload?.body?.data?.invoice_id,
            payment_link_id: paymentLinkId,
          })
        );
      } else {
        setFailedReason(res?.payload?.body?.data?.message);
        setPaymentProcess("false");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "failed",
            activity_type: "payment_activity",
            clusterid: userData?.data?.accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: res?.payload?.body?.data?.amount,
            order_id: res?.payload?.body?.data?.invoice_id,
            payment_link_id: paymentLinkId,
          })
        );
      }
      setIsScreenLoading(false);
    } catch (error: any) {
      setPaymentProcess("false");
      setFailedReason("Payment failed");
      setIsScreenLoading(false);
      console.log("handleApprove error =====", error);
    } finally {
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  const verifyPay = async (method: string) => {
    let slug = decode(actionId || "")?.split("_");
    try {
      let payload = {
        slug: actionId || "",
        lead_id:
          paymentLinkData?.lead_id ||
          (accountLeadData && accountLeadData?.lead_id
            ? accountLeadData?.lead_id
            : 0),
        cluster_id: slug[1],
        isrecurring: "N",
        x_payment_method: method,
        amount: searchParams.get("amount"),
        accountid: userData?.data?.accountData?.accountid || "",
        first_name: userData?.data?.accountData?.first_name || "",
        last_name: userData?.data?.accountData?.last_name || "",
        phone_number: userData?.data?.accountData.phone,
        module: "process_payment",
        transaction_id: searchParams.get("payment_intent"),
        isPaymentLink: true,
        payment_link: slug[0],
        conv_fee: conFee,
        description: "Online Payment",
        payment_note: "One-time",
        custom_conv: searchParams.get("custom_conv") || "off",
      };
      let res: any = await dispatch(verifyWalletPay(payload)).unwrap();
      if (res?.body?.success) {
        setInvoiceId(res?.body?.data?.invoice_id);
        setEmail(res?.body?.data?.email);
        setAmount(res?.body?.data?.total_amount);
        setEditVal(res?.body?.data?.total_amount);
        if (!res?.body?.data?.isDuplicate) {
          let pay = {
            emails: [userData?.data?.accountData?.email],
            invoice_id: res?.body?.data?.invoice_id,
          };
          await sendEmail(pay);
          setPaymentProcess("true");
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "succeeded",
              activity_type: "payment_activity",
              clusterid: userData?.data?.accountData?.clusterid,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: res?.body?.data?.amount,
              order_id: res?.body?.data?.invoice_id,
              payment_link_id: paymentLinkId,
            })
          );
        }
      } else {
        setFailedReason(res?.body?.data?.msg);
        setPaymentProcess("false");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "failed",
            activity_type: "payment_activity",
            clusterid: userData?.data?.accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: res?.body?.data?.amount,
            order_id: res?.body?.data?.invoice_id,
            payment_link_id: paymentLinkId,
          })
        );
      }
      setIsScreenLoading(false);
    } catch (error: any) {
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      setIsScreenLoading(false);
      console.log("verifyPay error =====", error);
    } finally {
      dispatch(setIsPaymentProcessing(false));
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  useEffect(() => {
    //cash app
    if (
      ((searchParams.has("payment_intent") ||
        searchParams.has("setup_intent")) &&
        !searchParams.has("checked") &&
        searchParams.has("method") &&
        searchParams.get("method") === "cashApp") ||
      (searchParams.get("method") === "card" && searchParams.has("amount"))
    ) {
      setIsScreenLoading(true);
      setInitialDisclosureAccept(true);
      searchParams.set("checked", "true");
      if (searchParams.get("isRecurring") === "false") {
        setEditVal(searchParams.get("amount") ?? "0.00");
      }
      verifyPay(searchParams.get("method") === "cashApp" ? "CAPP" : "CC");
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
    // stripe bank
    if (
      (searchParams.has("payment_intent") ||
        searchParams.has("setup_intent")) &&
      !searchParams.has("checked") &&
      searchParams.has("method") &&
      searchParams.get("method") === "bank" &&
      searchParams.has("amount")
    ) {
      setIsScreenLoading(true);
      setInitialDisclosureAccept(true);
      searchParams.set("checked", "true");
      if (searchParams.get("isRecurring") === "false") {
        setEditVal(searchParams.get("amount") ?? "0.00");
      }
      verifyPay("ACH");
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }

    //AMAZON PAY
    if (
      (searchParams.has("payment_intent") ||
        searchParams.has("setup_intent")) &&
      !searchParams.has("checked") &&
      searchParams.has("method") &&
      searchParams.get("method") === "amazonPay" &&
      searchParams.has("amount")
    ) {
      setIsScreenLoading(true);
      setInitialDisclosureAccept(true);
      searchParams.set("checked", "true");
      if (searchParams.get("isRecurring") === "false") {
        setEditVal(searchParams.get("amount") ?? "0.00");
      }
      verifyPay("AMZP");
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }

    // paypal
    if (searchParams.has("token") && !searchParams.has("checked")) {
      if (searchParams.get("status") === "cancel") {
        setPaymentProcess("false");
        setIsScreenLoading(false);
        setInitialDisclosureAccept(true);
        dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "failed",
            activity_type: "payment_activity",
            clusterid: userData?.data?.accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: payPalOrder?.amount,
            order_id: payPalOrder?.orderId,
            payment_link_id: paymentLinkId,
          })
        );
        dispatch(onSetFinalDisclosureLoading(false));
        dispatch(onSetShowDisclosure(false));
        setTimeout(() => {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          navigate({ search: "" });
        }, 500);
      } else {
        setIsScreenLoading(true);
        setInitialDisclosureAccept(true);
        searchParams.set("checked", "true");
        setEditVal(payPalOrder.amount);
        handleApprove({
          paymentId: searchParams.get("paymentId"),
          payerID: searchParams.get("PayerID"),
        });
        setTimeout(() => {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          navigate({ search: "" });
        }, 500);
      }
    }
  }, []);

  const handleApplePay = async (
    success: boolean,
    paymentIntent: any,
    failureMessage?: string
  ) => {
    const intentId = paymentIntent?.id;
    const decodedLink = decodeAndValidatePublicLink(actionId, "pl_");
    if (
      !success ||
      typeof intentId !== "string" ||
      !intentId.startsWith("pi_") ||
      !decodedLink
    ) {
      console.error("[Stripe Apple Pay][payment-link] invalid confirmation", {
        hasIntent: Boolean(intentId),
        intentType: intentId?.split("_")?.[0] || "unknown",
        hasValidLink: Boolean(decodedLink),
        success,
      });
      setFailedReason(failureMessage || "Payment failed");
      setPaymentProcess("false");
      setIsScreenLoading(false);
      dispatch(setIsPaymentProcessing(false));
      return;
    }

    const verificationKey = `AP:${intentId}`;
    if (applePayVerificationInFlightRef.current.has(verificationKey)) {
      console.info(
        "[Stripe Apple Pay][payment-link] duplicate verification skipped"
      );
      return;
    }
    applePayVerificationInFlightRef.current.add(verificationKey);
    let verificationCompleted = false;
    try {
      dispatch(setIsPaymentProcessing(true));
      setIsScreenLoading(true);
      const payload = {
        slug: actionId || "",
        lead_id: paymentLinkData?.lead_id,
        cluster_id: decodedLink.clusterId,
        isrecurring: "N",
        x_payment_method: "AP",
        amount: Number(String(editVal).replace(/,/g, "")),
        accountid: userData?.data?.accountData?.accountid || "",
        first_name: userData?.data?.accountData?.first_name || "",
        last_name: userData?.data?.accountData?.last_name || "",
        phone_number: userData?.data?.accountData.phone,
        module: "process_payment",
        transaction_id: intentId,
        isPaymentLink: true,
        payment_link: decodedLink.recordId,
        conv_fee: conFee,
        description: "Online Payment",
        payment_note: "One-time",
        custom_conv:
          paymentLinkData?.custom_conv === "" ||
          paymentLinkData?.custom_conv === undefined ||
          paymentLinkData?.custom_conv === null
            ? "off"
            : paymentLinkData?.custom_conv,
      };
      console.info("[Stripe Apple Pay][payment-link] verification requested", {
        intentType: intentId.split("_")[0],
        paymentMethod: payload.x_payment_method,
      });
      const res: any = await dispatch(verifyWalletPay(payload));
      const responseBody = res?.payload?.body;
      if (responseBody?.success) {
        verificationCompleted = true;
        const responseData = responseBody?.data;
        setInvoiceId(responseData?.invoice_id);
        setEmail(responseData?.email);
        setAmount(responseData?.total_amount);
        setEditVal(responseData?.total_amount);
        setPaymentProcess("true");

        if (!responseData?.isDuplicate) {
          try {
            await dispatch(
              addNotification({
                initiated_by: "contact",
                event_type: "payment_processed",
                userid: userData?.data?.accountData?.leadData?.user_id,
                email: "",
                event_status: "succeeded",
                activity_type: "payment_activity",
                clusterid: decodedLink.clusterId,
                communication_status: "",
                accounts_id: userData?.data?.accountData?.id,
                payment_amount: responseData?.amount,
                order_id: responseData?.invoice_id,
                payment_link_id: paymentLinkId,
              })
            );
            await sendEmail({
              emails: [
                responseData?.email || userData?.data?.accountData?.email,
              ],
              invoice_id: responseData?.invoice_id,
            });
          } catch (notificationError) {
            console.error(
              "[Stripe Apple Pay][payment-link] receipt notification failed",
              {
                message:
                  notificationError instanceof Error
                    ? notificationError.message
                    : String(notificationError),
              }
            );
          }
        }
      } else {
        setFailedReason(
          responseBody?.data?.msg ||
            responseBody?.data?.message ||
            "Payment failed"
        );
        setPaymentProcess("false");
      }
    } catch (error) {
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      console.error("[Stripe Apple Pay][payment-link] verification failed", {
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      if (!verificationCompleted) {
        applePayVerificationInFlightRef.current.delete(verificationKey);
      }
      setIsScreenLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const handleGooglePay = async (paymentIntent: any, totalAmount: any) => {
    if (paymentIntent?.tokenizationData) {
      await handleLegacyGooglePay(paymentIntent, totalAmount);
      return;
    }

    if (!paymentIntent?.id || !String(paymentIntent.id).startsWith("seti_")) {
      console.error("[Stripe Google Pay][payment-link] invalid SetupIntent", {
        hasIntent: Boolean(paymentIntent?.id),
        intentType: paymentIntent?.id?.split("_")?.[0] || "unknown",
      });
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      setIsGoogleLoading(false);
      dispatch(setIsPaymentProcessing(false));
      return;
    }

    const slug = decode(actionId || "")?.split("_");
    try {
      dispatch(setIsPaymentProcessing(true));
      setIsGoogleLoading(true);

      const payload = {
        slug: actionId || "",
        lead_id: paymentLinkData?.lead_id,
        cluster_id: slug?.[1] || userData?.data?.accountData?.clusterid || "",
        isrecurring: "N",
        x_payment_method: "GP",
        amount: Number(String(editVal).replace(/,/g, "")),
        accountid: userData?.data?.accountData?.accountid || "",
        first_name: userData?.data?.accountData?.first_name || "",
        last_name: userData?.data?.accountData?.last_name || "",
        phone_number: userData?.data?.accountData?.phone || "",
        module: "process_payment",
        transaction_id: paymentIntent.id,
        isPaymentLink: true,
        payment_link: slug?.[0] || "",
        conv_fee: conFee,
        description: "Online Payment",
        payment_note: "One-time",
        custom_conv:
          paymentLinkData?.custom_conv === "" ||
          paymentLinkData?.custom_conv === undefined ||
          paymentLinkData?.custom_conv === null
            ? "off"
            : paymentLinkData.custom_conv,
      };

      console.info("[Stripe Google Pay][payment-link] verification requested", {
        hasTransactionId: true,
        intentType: paymentIntent.id.split("_")[0],
        paymentMethod: payload.x_payment_method,
      });

      const res: any = await dispatch(verifyWalletPay(payload));
      const responseBody = res?.payload?.body;

      if (responseBody?.success) {
        const responseData = responseBody?.data;
        setInvoiceId(responseData?.invoice_id);
        setEmail(responseData?.email);
        setAmount(responseData?.total_amount);
        setEditVal(responseData?.total_amount);
        setPaymentProcess("true");

        if (!responseData?.isDuplicate) {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "succeeded",
              activity_type: "payment_activity",
              clusterid: userData?.data?.accountData?.clusterid,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: responseData?.amount,
              order_id: responseData?.invoice_id,
              payment_link_id: paymentLinkId,
            })
          );
          await sendEmail({
            invoice_id: responseData?.invoice_id,
            emails: [responseData?.email || userData?.data?.accountData?.email],
          });
        }
      } else {
        setFailedReason(
          responseBody?.data?.msg ||
            responseBody?.data?.message ||
            "Payment failed"
        );
        setPaymentProcess("false");
      }
    } catch (error) {
      setFailedReason("Payment failed");
      setPaymentProcess("false");
      console.error("[Stripe Google Pay][payment-link] verification failed", {
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsGoogleLoading(false);
      setIsScreenLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      dispatch(setIsPaymentProcessing(false));
    }
  };

  useEffect(() => {
    if (!actionId) return;
    const requestOptions = {
      method: "GET",
      redirect: "follow",
    };

    fetch(
      `${BASE_URL}/portal/get-payment-status?slug=${actionId}&type=payment_link`,
      requestOptions as any
    )
      .then((response) =>
        handleStreamResponse(response, (res: any) => {
          const match = res.match(
            /"success":\s*(true|false).*?"order_id":\s*(\d+).*?"amount":"(.*?)".*?"total_amount":"(.*?)".*?"email":"(.*?)".*?"lead_id":\s*(\d+)/
          );
          if (match) {
            console.log(match[1]);
            if (match[1] === "true") {
              setPaymentProcess("true");
              setInvoiceId(match[2]);
              setEmail(match[5]);
              setAmount(match[4] || match[3]);
              setEditVal(match[4] || match[3]);
            }
          }
        })
      )
      .then((result) => console.log(result))
      .catch((error) => console.error(error));
  }, [actionId]);

  const handleStreamResponse = async (
    response: any,
    onSuccessCallback: any
  ) => {
    const reader = response.body.getReader();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log("Stream finished.");
          break;
        }
        const chunk: any = new TextDecoder().decode(value);

        onSuccessCallback(chunk);
      }
    } catch (error) {
      console.error("Error reading the stream", error);
      // isConnectionClosed(true); // Assume the connection is closed on error
    }
  };

  const generalMarkers = getGeneralDetailMarkers({
    conFee,
    paymentAmount: editVal,
    nextDate: moment().format("MM/DD/YYYY"),
    paymentLink: window.location.href,
  });

  const handlePlaidBankPayment = async (
    values: any,
    isCCTokenAvailable: boolean
  ): Promise<void> => {
    try {
      let payload = {
        ...values,
        x_payment_method: "ACH",
      };
      await paymentViaBankOrCard(payload, isCCTokenAvailable);
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const markIncompleteOrder = async (orderId: number, error: any) => {
    try {
      if (orderId) {
        let res: any = await dispatch(
          markIncompletePayment({
            orderId,
            cluster_id:
              paymentLinkData?.clusterid ||
              decode(actionId || "")?.split("_")?.[1],
            failed_reason: error.message,
          })
        ).unwrap();

        if (res?.body?.success) {
          setAmount(res?.body?.data?.amount + res?.body?.data?.cc_fee);
          let pay = {
            invoice_id: orderId,
            emails: [res?.payload?.body?.data?.email],
          };
          await sendEmail(pay);
        }
      }
      setFailedReason(error.message);
      setPaymentProcess("false");
    } catch (error) {
      console.log("markIncompleteOrder error ======", error);
    } finally {
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetShowDisclosure(false));
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const verifyStripeCardPayment = async ({
    intent,
    email,
    error,
    orderId,
    principalAmount,
  }: VerifyStripeCardPaymentParams) => {
    let slug = decode(actionId || "")?.split("_");
    if (error) {
      await markIncompleteOrder(orderId || 0, error);
    } else {
      try {
        let payload = {
          slug: actionId || "",
          lead_id: paymentLinkData?.lead_id || 0,
          cluster_id: slug[1],
          isrecurring: "N",
          x_payment_method: "CC",
          amount: principalAmount ?? Number(editVal),
          accountid: userData?.data?.accountData?.accountid || "",
          first_name: userData?.data?.accountData?.first_name || "",
          last_name: userData?.data?.accountData?.last_name || "",
          phone_number: userData?.data?.accountData.phone,
          module: "process_payment",
          transaction_id: intent,
          isPaymentLink: true,
          payment_link: slug[0],
          conv_fee: conFee,
          description: "Online Payment",
          payment_note: "One-time",
          incomplete_order_id: orderId,
          custom_conv:
            paymentLinkData?.custom_conv === "" ||
            paymentLinkData?.custom_conv === undefined ||
            paymentLinkData?.custom_conv === null
              ? "off"
              : paymentLinkData?.custom_conv,
        };
        let res: any = await dispatch(verifyWalletPay(payload)).unwrap();
        if (res?.body?.success) {
          setInvoiceId(res?.body?.data?.invoice_id);
          setEmail(
            email ||
              res?.body?.data?.email ||
              userData?.data?.accountData?.email
          );
          setAmount(res?.body?.data?.total_amount);
          setEditVal(res?.body?.data?.total_amount);
          if (!res?.body?.data?.isDuplicate) {
            let pay = {
              emails: [
                email ||
                  res?.body?.data?.email ||
                  userData?.data?.accountData?.email,
              ],
              invoice_id: res?.body?.data?.invoice_id,
            };
            await sendEmail(pay);
            setPaymentProcess("true");
            await dispatch(
              addNotification({
                initiated_by: "contact",
                event_type: "payment_processed",
                userid: userData?.data?.accountData?.leadData?.user_id,
                email: "",
                event_status: "succeeded",
                activity_type: "payment_activity",
                clusterid: userData?.data?.accountData?.clusterid,
                communication_status: "",
                accounts_id: userData?.data?.accountData?.id,
                payment_amount: res?.body?.data?.amount,
                order_id: res?.body?.data?.invoice_id,
                payment_link_id: paymentLinkId,
              })
            );
          }
        } else {
          setFailedReason(res?.body?.data?.msg);
          setPaymentProcess("false");
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "failed",
              activity_type: "payment_activity",
              clusterid: userData?.data?.accountData?.clusterid,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: res?.body?.data?.amount,
              order_id: res?.body?.data?.invoice_id,
              payment_link_id: paymentLinkId,
            })
          );
        }
      } catch (error) {
        setFailedReason("Payment failed");
        setPaymentProcess("false");
      } finally {
        dispatch(resetDuplicateOrder());
        dispatch(resetCheckDuplicatePayment());
        await dispatch(onSetFinalDisclosureLoading(false));
        await dispatch(onSetShowDisclosure(false));
      }
    }
  };

  const verifyStaxPayment = async ({
    cc_token,
    cc_token2,
    x_method,
  }: {
    cc_token?: string;
    cc_token2?: string;
    x_method?: string;
  }) => {
    let slug = decode(actionId || "")?.split("_");
    try {
      let payload = {
        slug: actionId || "",
        x_method,
        isrecurring: "N",
        cc_token,
        cc_token2,
        amount: Number(editVal),
        accountid: userData?.data?.accountData?.accountid || "",
        first_name: userData?.data?.accountData?.first_name || "",
        last_name: userData?.data?.accountData?.last_name || "",
        phone_number: userData?.data?.accountData.phone,
        module: "process_payment",
        lead_id: paymentLinkData?.lead_id || 0,
        clusterid: slug[1],
        isPaymentLink: true,
        payment_link: slug[0],
        description: "Online Payment",
        payment_note: "One-time",
        conv_fee: conFee,
        custom_conv:
          paymentLinkData?.custom_conv === "" ||
          paymentLinkData?.custom_conv === undefined ||
          paymentLinkData?.custom_conv === null
            ? "off"
            : paymentLinkData?.custom_conv,
      };

      let res: any = await dispatch(verifyPaymentsViaStax(payload)).unwrap();
      if (res?.body?.success) {
        setInvoiceId(res?.body?.data?.invoice_id);
        setEmail(
          email || res?.body?.data?.email || userData?.data?.accountData?.email
        );
        setAmount(res?.body?.data?.total_amount || res?.body?.data?.amount);
        setAmount(res?.body?.data?.total_amount || res?.body?.data?.amount);
        if (!res?.body?.data?.isDuplicate) {
          let pay = {
            emails: [
              email ||
                res?.body?.data?.email ||
                userData?.data?.accountData?.email,
            ],
            invoice_id: res?.body?.data?.invoice_id,
          };
          await sendEmail(pay);
          setPaymentProcess("true");
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "succeeded",
              activity_type: "payment_activity",
              clusterid: userData?.data?.accountData?.clusterid,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: res?.body?.data?.amount,
              order_id: res?.body?.data?.invoice_id,
              payment_link_id: paymentLinkId,
            })
          );
        }
      } else {
        setFailedReason(res?.body?.data?.msg);
        setPaymentProcess("false");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "failed",
            activity_type: "payment_activity",
            clusterid: userData?.data?.accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: res?.body?.data?.amount,
            order_id: res?.body?.data?.invoice_id,
            payment_link_id: paymentLinkId,
          })
        );
      }
    } catch (error) {
      console.log("verifyStaxPayment error =====", error);
      setFailedReason("Payment failed");
      setPaymentProcess("false");
    } finally {
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  const verifyStaxPayPal = async ({
    transactionId,
    error,
  }: {
    transactionId?: string;
    error?: any;
  }) => {
    let slug = decode(actionId || "")?.split("_");
    setIsStaxPayPalLoading(true);
    try {
      let payload = {
        slug: actionId || "",
        x_method: "PP",
        isrecurring: "N",
        transaction_id: transactionId,
        amount: Number(editVal),
        accountid: userData?.data?.accountData?.accountid || "",
        first_name: userData?.data?.accountData?.first_name || "",
        last_name: userData?.data?.accountData?.last_name || "",
        phone_number: userData?.data?.accountData.phone,
        module: "process_payment",
        lead_id: paymentLinkData?.lead_id || 0,
        clusterid: slug[1],
        isPaymentLink: true,
        payment_link: slug[0],
        description: "Online Payment",
        payment_note: "One-time",
        conv_fee: conFee,
        custom_conv:
          paymentLinkData?.custom_conv === "" ||
          paymentLinkData?.custom_conv === undefined ||
          paymentLinkData?.custom_conv === null
            ? "off"
            : paymentLinkData?.custom_conv,
      };

      let res: any = await dispatch(verifyPaymentsViaStax(payload)).unwrap();
      if (res?.body?.success) {
        setInvoiceId(res?.body?.data?.invoice_id);
        setEmail(
          email || res?.body?.data?.email || userData?.data?.accountData?.email
        );
        setAmount(res?.body?.data?.amount || res?.body?.data?.total_amount);
        setEditVal(res?.body?.data?.total_amount);
        if (!res?.body?.data?.isDuplicate) {
          let pay = {
            emails: [
              email ||
                res?.body?.data?.email ||
                userData?.data?.accountData?.email,
            ],
            invoice_id: res?.body?.data?.invoice_id,
          };
          await sendEmail(pay);
          setPaymentProcess("true");
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_processed",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "succeeded",
              activity_type: "payment_activity",
              clusterid: userData?.data?.accountData?.clusterid,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: res?.body?.data?.amount,
              order_id: res?.body?.data?.invoice_id,
              payment_link_id: paymentLinkId,
            })
          );
        }
      } else {
        setFailedReason(res?.body?.data?.msg);
        setPaymentProcess("false");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "payment_processed",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "failed",
            activity_type: "payment_activity",
            clusterid: userData?.data?.accountData?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: res?.body?.data?.amount,
            order_id: res?.body?.data?.invoice_id,
            payment_link_id: paymentLinkId,
          })
        );
      }
    } catch (error) {
      console.log("verifyStaxPayPal error =====", error);
      setFailedReason("Payment failed");
      setPaymentProcess("false");
    } finally {
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
      setIsStaxPayPalLoading(false);
    }
  };
  return (
    <div {...testAttrs("page", "Payment link")}>
      <MessageToast
        variant="warning"
        content={mismatchNotice}
        width="w-full max-w-[442px] px-5"
        className="!right-1/2 !translate-x-1/2"
        onCloseClick={() => setMismatchNotice(null)}
      />
      <div
        className={`w-full lg:h-[var(--shell-vh)] lg:grid grid-cols-2 lg:overflow-hidden`}
        {...testAttrs("layout", "Payment link layout")}
      >
        {/* PAYMENT DESCRIPTION PART */}
        <div
          style={{ backgroundColor: brandColor }}
          className="relative lg:h-[var(--shell-vh)] "
          {...testAttrs("details-panel", "Payment link details")}
        >
          {(!paymentProcess || paymentProcess !== "true") && (
            <div
              className={`lg:absolute hidden lg:top-0 
               w-full lg:flex justify-between items-center z-10 ${
                 isExpired(paymentLinkData?.expiry_date || "")
                   ? "bg-lightRed2"
                   : "bg-white"
               }  lg:px-6 py-[6px] border-r-[2px] border-bgSecondary px-5`}
            >
              <p className="lg:text-base text-sm font-medium text-txtPrimary w-[50%] break-words">
                Payment link{" "}
                {isExpired(paymentLinkData?.expiry_date || "")
                  ? "expired"
                  : "expires"}{" "}
                on{" "}
                {isLoading ? (
                  <Skeleton width={100} />
                ) : (
                  <span
                    className={`font-bold inline-block whitespace-nowrap ${
                      isExpired(paymentLinkData?.expiry_date || "")
                        ? "text-red"
                        : "text-btnPrimaryActive"
                    } `}
                  >
                    {formatPaymentListDate(
                      paymentLinkData?.expiry_date || "",
                      paymentLinkData?.pl_tz || "",
                      "long"
                    )}
                  </span>
                )}
              </p>
              <Countdown
                isLoading={isLoading}
                seconds={seconds}
                isExpired={isExpired(paymentLinkData?.expiry_date || "")}
              />
            </div>
          )}
          <div
            className={`classScroll w-full ${
              !paymentProcess || paymentProcess !== "true"
                ? "lg:mt-[110px] md:mt-[45px] mt-[40px]"
                : "lg:mt-[50px] md:mt-[45px] mt-[40px]"
            } lg:h-full lg:overflow-y-auto lg:max-h-[calc(var(--shell-vh)-120px)] py-5`}
            {...testAttrs("details-scroll", "Payment link details scroll area")}
          >
            <div className="flex flex-col lg:flex-row justify-center lg:mt-[7%] h-fit">
              {(!paymentProcess || paymentProcess !== "true") && (
                <div
                  className={`lg:hidden 
               w-full flex justify-between items-center mb-2 ${
                 isExpired(paymentLinkData?.expiry_date || "")
                   ? "bg-lightRed2"
                   : "bg-white"
               }   py-[6px] border-r-[2px] border-bgSecondary px-5`}
                >
                  <p className="lg:text-base text-sm font-medium text-txtPrimary w-[50%] break-words">
                    Payment link{" "}
                    {isExpired(paymentLinkData?.expiry_date || "")
                      ? "expired"
                      : "expires"}{" "}
                    on{" "}
                    {isLoading ? (
                      <Skeleton width={100} />
                    ) : (
                      <span
                        className={`font-bold inline-block whitespace-nowrap ${
                          isExpired(paymentLinkData?.expiry_date || "")
                            ? "text-red"
                            : "text-btnPrimaryActive"
                        } `}
                      >
                        {formatPaymentListDate(
                          paymentLinkData?.expiry_date || "",
                          paymentLinkData?.pl_tz || "",
                          "long"
                        )}
                      </span>
                    )}
                  </p>
                  <Countdown
                    isLoading={isLoading}
                    seconds={seconds}
                    isExpired={isExpired(paymentLinkData?.expiry_date || "")}
                  />
                </div>
              )}
              <div
                className={`flex flex-col items-start lg:gap-10 gap-4 w-full lg:max-w-[380px] lg:pt-0 px-5 pt-5`}
                {...testAttrs("payment-summary", "Payment link summary")}
              >
                <div className="lg:flex hidden items-center gap-[10px]">
                  {isLoading ? (
                    <Skeleton width={100} height={35} />
                  ) : (
                    <div className="h-16 lg:h-16 flex justify-end">
                      <img
                        src={paymentLogo}
                        alt="Company logo"
                        className="animate__animated animate__fadeIn object-contain"
                      />
                    </div>
                  )}
                </div>
                {/* PAYMENT TYPE AND AMOUNT */}
                <div className={`flex flex-col items-start w-full`}>
                  <span className="w-full flex justify-between items-center">
                    <div
                      className={`${
                        isDark ? "text-darktxtParagraph" : "text-txtParagraph"
                      } font-bold text-xl leading-[26.04px]`}
                    >
                      Payment Link
                    </div>
                    <div
                      className={`lg:hidden text-base font-medium underline ${
                        isDark
                          ? "text-darkTxtBlue hover:!text-darkBlueHover"
                          : "text-blue hover:!text-btnPrimaryHover"
                      } cursor-pointer`}
                      role="button"
                      tabIndex={0}
                      {...testAttrs(
                        "toggle-details",
                        showDetails
                          ? "Hide payment link details"
                          : "View payment link details"
                      )}
                      onClick={() => setShowDetails(!showDetails)}
                    >
                      {showDetails ? "Hide details" : "View details"}
                    </div>
                  </span>
                  <div
                    className="flex justify-between w-full gap-1 lg:mt-4 mt-6 relative"
                    role="button"
                    tabIndex={0}
                    {...testAttrs("amount-section", "Payment link amount")}
                    onClick={handleClick}
                  >
                    {isEdit && paymentLinkData?.is_editable === "Y" ? (
                      <div
                        className="max-w-[240px] lg:w-full w-[250px] relative self-end"
                        ref={amtInputContainerRef}
                      >
                        <CustomAmountInput
                          data-testid="payment-link-amount-input"
                          aria-label="Payment link amount"
                          checkAmt={false}
                          isCheckAllowed={false}
                          inputValue={editVal}
                          balance={userData?.data?.accountData?.balance}
                          setinputValue={(e: any) => {
                            dispatch(setIsPaymentProcessing(true));
                            setAmtErr(null);
                            setEditVal(e);
                          }}
                          className={`lg:!text-[50px] !text-[32px] lg:!leading-[65.1px] leading-[41.66px] font-bold text-txtPrimary lg:!h-[65px] !h-[42px] lg:!pl-[45px] !pl-[32px] ${
                            amtErr
                              ? "!shadow-[0_0_4px_0px_rgba(255,0,0,1)]"
                              : "!shadow-[0_0_4px_0px_rgba(22,120,242,1)]"
                          }`}
                          _className={`lg:!text-[50px] !text-[32px] lg:!leading-[65.1px] leading-[41.66px] font-bold text-txtPrimary !top-0  left-[12px]`}
                        />

                        {amtErr && (
                          <p className="pt-4 text-red text-sm leading-[18.23px]">
                            {amtErr}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="flex gap-6 self-end">
                        <div
                          className={`font-bold lg:leading-[65.1px] leading-[41.66px] ${
                            isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                          } lg:text-[44px] text-[32px]`}
                        >
                          {formatCurrency(editVal)}
                        </div>
                        {paymentLinkData?.is_editable === "Y" &&
                          !paymentProcess && (
                            <div className="flex items-center ">
                              <EditIcon
                                aria-hidden="true"
                                strokeColor={
                                  isDark
                                    ? "var(--color-icon-dark-bright)"
                                    : "var(--color-icon-bright)"
                                }
                                className="group "
                                _className={
                                  isDark
                                    ? "group-hover:!fill-darkTxtPrimary"
                                    : "group-hover:!fill-iconObscure"
                                }
                              />
                            </div>
                          )}
                      </div>
                    )}
                    <div className={`min-w-[80px] self-start`}>
                      {!isLoading && (
                        <img
                          src={qrCode || ScannerImg}
                          alt="Payment link QR code"
                          className={`lg:h-[80px] lg:w-[80px] h-[60px] w-[60px]  ${
                            isDark ? "invert" : "mix-blend-multiply"
                          }`}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <span
                  className={`drawer ${
                    showDetails && "open"
                  } flex flex-col w-full gap-5`}
                  {...testAttrs(
                    "account-details",
                    "Payment link account details"
                  )}
                >
                  {/* LINE */}
                  <div className="h-[1px] bg-strokeActive w-full" />
                  {/* ACCOUNT DETAILS */}
                  <div className="flex flex-col gap-4 w-full">
                    <div className="flex flex-col gap-3 lg:max-w-[380px] w-full">
                      <div className="flex justify-between items-center">
                        <div
                          className={`leading-[26.04px] ${
                            isDark
                              ? "text-darkTxtBlue"
                              : "text-btnPrimaryActive"
                          } font-medium text-xl`}
                        >
                          {isLoading ? (
                            <Skeleton width={150} />
                          ) : (
                            (userData?.data?.accountData?.first_name || "") +
                            " " +
                            (userData?.data?.accountData?.last_name || "")
                          )}
                        </div>
                        <div
                          className={`text-sm ${
                            isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                          }`}
                        >
                          {isLoading ? (
                            <Skeleton width={80} />
                          ) : (
                            userData?.data?.accountData?.accountid
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <div className="grid grid-cols-2 w-full">
                          <div
                            className={`leading-[20px] ${
                              isDark
                                ? "text-darkTxtSecondary"
                                : "text-txtSecondary"
                            } text-sm text-left`}
                          >
                            Balance
                          </div>
                          <div
                            className={`leading-[20px] ${
                              isDark
                                ? "text-darkTxtSecondary"
                                : "text-txtSecondary"
                            } text-sm text-right`}
                          >
                            Description
                          </div>
                        </div>
                        <div className="grid grid-cols-2 w-full">
                          <p
                            className={`leading-[20px] ${
                              isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                            } text-sm text-left`}
                          >
                            {isLoading ? (
                              <Skeleton width={70} />
                            ) : (
                              <>
                                {formatCurrency(
                                  userData?.data?.accountData?.balance
                                ) || "$0.00"}
                              </>
                            )}
                          </p>
                          <p
                            className={`leading-[20px] ${
                              isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                            } text-sm text-right`}
                          >
                            {isLoading ? (
                              <Skeleton width={80} />
                            ) : (
                              (userData?.data?.accountData?.description ?? "--")
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* LINE */}
                    <div className="h-[1px] bg-strokeActive w-full" />
                    <div className="flex flex-col gap-3 lg:max-w-[380px] w-full">
                      <div className="flex flex-col items-start gap-1">
                        <div className="grid grid-cols-2 w-full">
                          <div
                            className={`leading-[20px] ${
                              isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                            } text-sm text-left`}
                          >
                            DUE DATE
                          </div>
                        </div>
                        <div className="grid grid-cols-2 w-full">
                          <div
                            className={`leading-[20px] ${
                              isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                            } text-sm text-left`}
                          >
                            {isLoading ? (
                              <Skeleton width={70} />
                            ) : (
                              <>
                                {formatPaymentListDate(
                                  paymentLinkData?.due_date || "",
                                  paymentLinkData?.pl_tz || "",
                                  "long"
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </span>
                <Footer
                  style={{ backgroundColor: brandColor }}
                  className={`lg:!flex !hidden !w-[30%] max-w-[380px] !justify-start ${
                    isDark ? "!text-darktxtParagraph" : "!text-txtParagraph"
                  }`}
                />
              </div>
            </div>
          </div>
        </div>
        {/* PAYMENT METHOD SECTION */}
        {isScreenLoading ? (
          <div
            className="h-full max-h-[var(--shell-vh)] flex justify-center items-center"
            {...testAttrs("processing-loading", "Processing payment link")}
          >
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
          </div>
        ) : (
          <div
            ref={paymentSectionRef}
            className={`w-full lg:h-[var(--shell-vh)] lg:overflow-y-auto`}
            {...testAttrs("payment-section", "Payment link payment section")}
          >
            {!isLoading &&
              isExpired(paymentLinkData?.expiry_date || "") &&
              !paymentProcess && (
                <>
                  {isLinkSent ? (
                    <PaymentLinkSuccess
                      success={isLinkSentSuccess}
                      requestStatus={requestStatus}
                      failedReason={isLinkSentFailedReason}
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center">
                      <PaymentLinkExpired
                        setIsLinkSent={setIsLinkSent}
                        setIsLinkSentSuccess={setIsLinkSentSuccess}
                        setIsLinkSentFailedReason={setIsLinkSentFailedReason}
                        className={"lg:mt-0 mt-[20px]"}
                      />
                    </div>
                  )}
                </>
              )}
            {!isLoading &&
              (paymentProcess ? (
                paymentProcess === "true" ? (
                  isWirePayment ? (
                    <WirePaymentSuccess
                      bankDetails={wireDetails}
                      orderId={invoiceId}
                      lead_id={paymentLinkData?.lead_id ?? ""}
                      sendEmail={sendEmail}
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center">
                      <PaymentLinkProcessSuccess
                        email={email}
                        amount={amount}
                        invoiceId={invoiceId}
                        sendEmail={sendEmail}
                        className={"lg:mt-0 mt-7 px-5"}
                        lead_id={paymentLinkData?.lead_id ?? ""}
                      />
                    </div>
                  )
                ) : (
                  <PaymentLinkProcessFailed
                    failedReason={failedReason}
                    handleBackToStart={handleBackToStart}
                  />
                )
              ) : (
                !isExpired(paymentLinkData?.expiry_date || "") &&
                (portalSettings?.state_disclosure_flag === SettingsValue.OFF ||
                initialDisclosureAccept ||
                !isLogin ? (
                  <div
                    className={
                      showFinalDisclosure
                        ? "h-full "
                        : "relative w-full lg:pt-[110px] lg:px-10 px-5 1xl:pl-[120px] pt-5 lg:pb-10 pb-9"
                    }
                    {...testAttrs(
                      "payment-methods-region",
                      "Payment link payment methods"
                    )}
                  >
                    <PaymentMethods
                      generalMarkers={generalMarkers}
                      currentStep={3}
                      setCurrentStep={() => {}}
                      verifyStripeCardPayment={verifyStripeCardPayment}
                      handleNext={processPaymentLink}
                      handleBack={() => null}
                      custom_conv={
                        paymentLinkData?.custom_conv === "" ||
                        paymentLinkData?.custom_conv === undefined ||
                        paymentLinkData?.custom_conv === null
                          ? "off"
                          : paymentLinkData?.custom_conv
                      }
                      tempData={{
                        amount: editVal,
                      }}
                      isPaymentLink={true}
                      useStripeGooglePay={true}
                      useStripeApplePay={true}
                      paymentMethods={paymentLinkData?.payment_methods}
                      handleGooglePay={handleGooglePay}
                      amount={editVal}
                      createPayPalIntent={createPayPalIntent}
                      isGoogleLoading={isGoogleLoading}
                      isLoading={isPaymentLinkBTNLoading}
                      selectedMethod={selectedMethod}
                      setSelectedMethod={setSelectedMethod}
                      handleApplePay={handleApplePay}
                      setAmtErr={setAmtErr}
                      setIsEdit={setIsEdit}
                      amtErr={amtErr}
                      isEdit={isEdit}
                      cashAppRef={cashAppRef}
                      cardRef={cardRef}
                      bankRef={bankRef}
                      amazonRef={amazonRef}
                      tokenData={tokenData}
                      setTokenData={setTokenData}
                      orderData={paymentLinkData}
                      handlePlaidBankPayment={handlePlaidBankPayment}
                      isManualForm={isManualForm}
                      setIsManualForm={setIsManualForm}
                      setIsLoading={setIsPaymentLinkBTNLoading}
                      setIsGoogleLoading={setIsGoogleLoading}
                      verifyStaxPayment={verifyStaxPayment}
                      verifyStaxPayPal={verifyStaxPayPal}
                      isStaxPayPalLoading={isStaxPayPalLoading}
                    />
                  </div>
                ) : (
                  <div
                    className="h-full lg:max-h-[var(--shell-vh)] relative content w-full lg:pt-20 lg:pb-[40px] 1xl:px-20 xl:px-14 lg:px-10 md:p-10 p-5"
                    {...testAttrs(
                      "initial-disclosure",
                      "Initial payment disclosure"
                    )}
                  >
                    <DisclosureInitial
                      isOpen={true}
                      handleAccept={() => {
                        setInitialDisclosureAccept(true);
                        scrollPaymentSectionToTop();
                      }}
                      generalMarkers={generalMarkers}
                    />
                  </div>
                ))
              ))}
          </div>
        )}
      </div>
      <CustomModal
        signModal={false}
        isLoading={false}
        isOpen={showModal}
        title="Alert"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        doneText="Leave"
        cancleText="Stay"
        onDoneClick={handleConfirmLeave}
        onCloseClick={handleCancelLeave}
        children={
          <React.Fragment>
            <div className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
              Are you sure you want to leave the page? All progress will be
              lost.
            </div>
          </React.Fragment>
        }
      />
    </div>
  );
};

export default PaymentLink;
